import React, { useState, useEffect, useRef } from "react";
import { Icons } from "./icons.jsx";
import { Pill, ActionButton, LiveDot } from "./primitives.jsx";
import { useStartDemoSimulation } from "./useMarketplace.js";
import {
  IconBtnGhostWhite,
  PreviewModalBackdrop,
  PreviewModalPanel,
  PreviewModalCloseBtn,
  PreviewModalVideoWrap,
  PreviewModalVideoEl,
  PreviewModalVideoGradient,
  PreviewModalVideoControls,
  PreviewModalLiveBadge,
  PreviewModalBody,
  PreviewModalPillRow,
  PreviewModalTitle,
  PreviewModalDesc,
  PreviewModalStatsGrid,
  PreviewModalStatItem,
  PreviewModalStatNum,
  PreviewModalStatLabel,
  PreviewModalActionRow,
  PreviewModalFootnote,
} from "./style.js";

// ── PreviewModal ──────────────────────────────────────────────────────────────
const PreviewModal = ({ program, onClose, onSimulationStarted }) => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(false);
  const demoMutation = useStartDemoSimulation();

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  if (!program) return null;
  const [accentStart, accentEnd] = program.accent ?? ["#01ECFF", "#962EFF"];

  return (
    <PreviewModalBackdrop onClick={onClose}>
      <PreviewModalPanel onClick={(e) => e.stopPropagation()}>
        <PreviewModalCloseBtn onClick={onClose}>
          <Icons.x size={20} />
        </PreviewModalCloseBtn>
        <PreviewModalVideoWrap $a={accentStart} $b={accentEnd}>
          <PreviewModalVideoEl
            ref={videoRef}
            src={program.intro_video_url}
            autoPlay
            muted={muted}
            loop
            playsInline
          />
          <PreviewModalVideoGradient />
          <PreviewModalVideoControls>
            <IconBtnGhostWhite onClick={() => setMuted((m) => !m)}>
              {muted ? "🔇" : "🔊"}
            </IconBtnGhostWhite>
          </PreviewModalVideoControls>
          <PreviewModalLiveBadge>
            <LiveDot /> <span>Intro from {program.guru?.full_name}</span>
          </PreviewModalLiveBadge>
        </PreviewModalVideoWrap>
        <PreviewModalBody>
          <PreviewModalPillRow>
            <Pill tone="turq">{program.categories?.[0]?.name ?? ""}</Pill>
            <Pill tone="success">⚡ Free demo</Pill>
          </PreviewModalPillRow>
          <PreviewModalTitle>{program.name}</PreviewModalTitle>
          <PreviewModalDesc>
            Try a real scenario before enrolling. You'll be live with{" "}
            {program.guru?.full_name.split(" ")[0]}, our AI Guru, who'll throw a
            realistic situation at you and give instant feedback. Takes ~5
            minutes.
          </PreviewModalDesc>
          <PreviewModalStatsGrid>
            {[
              [program.duration, "Total length"],
              [program.lesson_count, "Skill Sessions"],
              [program.sim_count, "Simulations"],
              [program.rating, `${program.review_count} reviews`],
            ].map(([num, lbl]) => (
              <PreviewModalStatItem key={lbl}>
                <PreviewModalStatNum>{num}</PreviewModalStatNum>
                <PreviewModalStatLabel>{lbl}</PreviewModalStatLabel>
              </PreviewModalStatItem>
            ))}
          </PreviewModalStatsGrid>
          <PreviewModalActionRow>
            <ActionButton
              variant="primary"
              size="lg"
              icon={<Icons.zap size={16} />}
              disabled={demoMutation.isPending || !program.demo_scenario_id}
              onClick={() => {
                if (!program.demo_scenario_id) return;
                demoMutation.mutate(program.id, {
                  onSuccess: (data) => {
                    onClose();
                    onSimulationStarted?.(data);
                  },
                });
              }}
            >
              {demoMutation.isPending ? "Starting…" : "Start demo simulation"}
            </ActionButton>
            {!program.demo_scenario_id && (
              <span style={{ fontSize: 12, color: "#A87EFF", alignSelf: "center" }}>
                Demo coming soon
              </span>
            )}
          </PreviewModalActionRow>
          {demoMutation.isError && (
            <p style={{ fontSize: 12, color: "#FF6B6B", margin: "8px 0 0" }}>
              {demoMutation.error?.message ?? "Failed to start demo. Please try again."}
            </p>
          )}
          <PreviewModalFootnote>
            Learn by doing, not just watching · No credit card · 5 mins
          </PreviewModalFootnote>
        </PreviewModalBody>
      </PreviewModalPanel>
    </PreviewModalBackdrop>
  );
};

export default PreviewModal;
